import { useCallback, useEffect, useId, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CloudUploadIcon, Loader2 } from 'lucide-react'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import DocumentRow from '../../DocumentCenter/components/DocumentRow'

const QUARTER_TITLE = {
  q12020: 'Q1 2020', q22020: 'Q2 2020', q32020: 'Q3 2020', q42020: 'Q4 2020',
  q12021: 'Q1 2021', q22021: 'Q2 2021', q32021: 'Q3 2021', q42021: 'Q4 2021'
}

/** Must match server `TRANSCRIPT_HOLD_105C_LEGACY_DOC_TYPE` — old hold uploads only. */
const HOLD_105C_LEGACY_DOC_TYPE = 'transcript-review-105c'

/**
 * Rich HTML from admin (lists, links). Tailwind preflight strips list markers on ul/ol;
 * these utilities restore bullets and numbering. Sync list styling with any other
 * transcript-hold HTML surfaces if added.
 */
const RICH_HTML_FROM_ADMIN =
  'text-sm text-slate-700 dark:text-slate-300 max-w-none [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_a]:text-ertc-primary [&_a]:underline'

const RICH_HTML_ACTION_BOX =
  `${RICH_HTML_FROM_ADMIN} text-amber-950 dark:text-amber-100 [&_a]:text-amber-800 dark:[&_a]:text-amber-200`

/** Bespoke portal flows; all other on-hold reasons use action HTML + Yes/No when `actionHtml` is set. Sync with transcriptReviewClientResponse.js `STRUCTURED_TRANSCRIPT_HOLD_REASON_KEYS`. */
const STRUCTURED_HOLD_REASON_KEYS = new Set([
  'letter_105c_sent',
  'letter_6042c_sent',
  'refund_previously_issued'
])

function isStructuredHoldReasonKey(rk) {
  return STRUCTURED_HOLD_REASON_KEYS.has(String(rk || ''))
}

function formatHoldRefundUsd(amount) {
  const n = Number(amount)
  if (Number.isFinite(n))
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  return amount != null && amount !== '' ? String(amount) : '—'
}

/** Calendar label for hold questionnaire copy (matches portal expectations for en-US). */
function formatHoldRefundDate(value) {
  if (value == null || value === '') return ''
  const s = String(value).trim()
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymd) {
    const y = Number(ymd[1])
    const m = Number(ymd[2])
    const d = Number(ymd[3])
    const dt = new Date(y, m - 1, d)
    if (!Number.isNaN(dt.getTime()))
      return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  }
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime()))
    return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  return s
}

export default function TranscriptReviewHoldPage() {
  const { applicationId, quarter } = useParams()
  const navigate = useNavigate()
  const baseId = useId()

  const [detail, setDetail] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [uploadBusy, setUploadBusy] = useState(false)

  const [hasCopy, setHasCopy] = useState('')
  const [has6042Copy, setHas6042Copy] = useState('')
  const [canProvide941x, setCanProvide941x] = useState('')
  /** `refundDetailId` → 'yes' | 'no' | '' — per IRS payment for refund_previously_issued */
  const [receivedByCheckId, setReceivedByCheckId] = useState({})
  const [genericActionYesNo, setGenericActionYesNo] = useState('')
  const [hold105cDocuments, setHold105cDocuments] = useState([])

  /** Matches `document_types.type` for LTR 105C in quarter documents (fallback if API omits). */
  const docType = detail?.hold105cDocumentType || '105c'

  const trimmedActionHtml = typeof detail?.actionHtml === 'string' ? detail.actionHtml.trim() : ''
  const showActionRequiredHtml = Boolean(trimmedActionHtml)
  const showGenericActionRequiredUi = Boolean(detail?.reasonKey && !isStructuredHoldReasonKey(detail.reasonKey) && trimmedActionHtml)

  const fetchDetail = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    const url = `${apiURL}/api/applications/${applicationId}/transcript-review-holds/${quarter}`
    const res = await fetch(url, { headers: { Authorization: account.accessToken } })
    const data = await res.json()
    if (data.response?.error === 'Failed To Authenticate Token.') {
      const refreshed = await refreshAccessToken()
      if (refreshed) return fetchDetail()
      navigate('/', { state: { expired: true } })
      return
    }
    if (!res.ok || data.response?.error) {
      setLoadError(data.response?.error || 'Could not load this page.')
      setDetail(null)
      setLoading(false)
      return
    }
    setDetail(data.response)
    setLoading(false)
  }, [applicationId, quarter, navigate])

  const refreshHold105cDocuments = useCallback(async () => {
    const types = docType === HOLD_105C_LEGACY_DOC_TYPE ? [docType] : [docType, HOLD_105C_LEGACY_DOC_TYPE]
    const uniqueTypes = [...new Set(types)]
    const byId = new Map()
    for (const t of uniqueTypes) {
      const url = `${apiURL}/api/applications/${applicationId}/documents?type=${encodeURIComponent(t)}`
      const res = await fetch(url, { headers: { Authorization: account.accessToken } })
      const data = await res.json()
      if (data.response?.documents) {
        for (const d of data.response.documents) {
          if (d.quarter === quarter) byId.set(d.documentId, d)
        }
      }
    }
    const merged = Array.from(byId.values()).sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return tb - ta
    })
    setHold105cDocuments(merged)
  }, [applicationId, quarter, docType])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  useEffect(() => {
    if (!detail?.reviewQuarterId) return
    setHasCopy('')
    setHas6042Copy('')
    setCanProvide941x('')
    setReceivedByCheckId({})
    setGenericActionYesNo('')
    setHold105cDocuments([])
  }, [detail?.reviewQuarterId])

  useEffect(() => {
    if (detail?.reasonKey === 'letter_105c_sent') refreshHold105cDocuments()
  }, [detail?.reasonKey, refreshHold105cDocuments])

  const onUpload = async (file) => {
    if (!file) return
    setUploadBusy(true)
    setSubmitError('')
    const form = new FormData()
    form.append('file', file)
    const url = `${apiURL}/api/applications/${applicationId}/documents?type=${encodeURIComponent(docType)}&quarter=${encodeURIComponent(quarter)}`
    const res = await fetch(url, { method: 'POST', headers: { Authorization: account.accessToken }, body: form })
    const data = await res.json()
    if (data.response?.error === 'Failed To Authenticate Token.') {
      const refreshed = await refreshAccessToken()
      if (refreshed) return onUpload(file)
      navigate('/', { state: { expired: true } })
      return
    }
    if (!res.ok || data.response?.error) {
      setSubmitError(data.response?.error || 'Upload failed.')
      setUploadBusy(false)
      return
    }
    await refreshHold105cDocuments()
    setUploadBusy(false)
  }

  const buildPayload = () => {
    const rk = detail?.reasonKey
    if (showGenericActionRequiredUi) {
      return {
        actionRequiredResponse: genericActionYesNo === 'yes' ? 'yes' : genericActionYesNo === 'no' ? 'no' : ''
      }
    }
    if (rk === 'letter_105c_sent') return { hasCopy: hasCopy === 'yes' ? 'yes' : hasCopy === 'no' ? 'no' : '' }
    if (rk === 'letter_6042c_sent') {
      const base = { has6042Copy: has6042Copy === 'yes' ? 'yes' : has6042Copy === 'no' ? 'no' : '' }
      if (base.has6042Copy === 'no')
        base.canProvide941x = canProvide941x === 'yes' ? 'yes' : canProvide941x === 'no' ? 'no' : ''
      return base
    }
    if (rk === 'refund_previously_issued') {
      const checks = Array.isArray(detail?.refundChecks) ? detail.refundChecks : []
      return {
        refundCheckResponses: checks.map((c) => {
          const id = String(c.refundDetailId || '')
          const v = receivedByCheckId[id]
          return {
            refundDetailId: id,
            received: v === 'yes' ? 'yes' : v === 'no' ? 'no' : ''
          }
        })
      }
    }
    return {}
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError('')
    const payload = buildPayload()
    const url = `${apiURL}/api/applications/${applicationId}/transcript-review-holds/${quarter}/submit`
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: account.accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewQuarterId: detail.reviewQuarterId, payload })
    })
    const data = await res.json()
    if (data.response?.error === 'Failed To Authenticate Token.') {
      const refreshed = await refreshAccessToken()
      if (refreshed) {
        setSubmitting(false)
        return handleSubmit()
      }
      setSubmitting(false)
      navigate('/', { state: { expired: true } })
      return
    }
    if (!res.ok || data.response?.error) {
      setSubmitError(data.response?.error || 'Could not save. Please try again.')
      setSubmitting(false)
      return
    }
    setSubmitting(false)
    navigate(`/applications/${applicationId}/quarter-breakdown`)
  }

  if (loading) {
    return (
      <div className='w-full flex justify-center items-center min-h-[40vh]'>
        <Loader2 className='w-8 h-8 animate-spin text-ertc-primary' aria-hidden='true' />
        <span className='sr-only'>Loading</span>
      </div>
    )
  }

  if (loadError || !detail) {
    return (
      <div className='w-full max-w-xl mx-auto p-6'>
        <Link to={`/applications/${applicationId}/quarter-breakdown`} className='inline-flex items-center gap-2 text-sm text-ertc-primary font-medium mb-4'>
          <ArrowLeft size={16} aria-hidden='true' /> Back to quarter breakdown
        </Link>
        <div role='alert' className='rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-4 text-amber-900 dark:text-amber-200 text-sm'>
          {loadError || 'Nothing to show.'}
        </div>
      </div>
    )
  }

  const title = QUARTER_TITLE[quarter] || quarter
  const rk = detail.reasonKey
  const show105cUpload = rk === 'letter_105c_sent' && hasCopy === 'yes'
  const refundBlocked = rk === 'refund_previously_issued' && !detail.refundFilled
  const refundChecksList = Array.isArray(detail.refundChecks) ? detail.refundChecks : []

  const canSubmitResponses = () => {
    if (refundBlocked) return false
    if (showGenericActionRequiredUi)
      return genericActionYesNo === 'yes' || genericActionYesNo === 'no'
    if (rk === 'letter_105c_sent') {
      if (hasCopy !== 'yes' && hasCopy !== 'no') return false
      if (hasCopy === 'yes' && hold105cDocuments.length < 1) return false
      return true
    }
    if (rk === 'letter_6042c_sent') {
      if (has6042Copy !== 'yes' && has6042Copy !== 'no') return false
      if (has6042Copy === 'no' && canProvide941x !== 'yes' && canProvide941x !== 'no') return false
      return true
    }
    if (rk === 'refund_previously_issued') {
      if (!refundChecksList.length) return false
      return refundChecksList.every((c) => {
        const id = String(c.refundDetailId || '')
        const v = receivedByCheckId[id]
        return v === 'yes' || v === 'no'
      })
    }
    return false
  }

  return (
    <div className='w-full flex justify-center p-4 sm:p-8 xl:p-12'>
      <div className='w-full max-w-2xl'>
        <Link to={`/applications/${applicationId}/quarter-breakdown`} className='inline-flex items-center gap-2 text-sm text-ertc-primary font-medium mb-6'>
          <ArrowLeft size={16} aria-hidden='true' /> Back to quarter breakdown
        </Link>
        <h1 className='text-2xl font-bold text-slate-900 dark:text-white mb-2'>Additional information — {title}</h1>
        <p className='text-slate-600 dark:text-slate-300 text-sm mb-6'>{detail.label}</p>

        <div
          className={`mb-8 border border-gray-100 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800 ${RICH_HTML_FROM_ADMIN}`}
          dangerouslySetInnerHTML={{ __html: detail.explanationHtml }}
        />

        {showActionRequiredHtml && (
          <div className='mb-6 rounded-xl border border-amber-400/40 dark:border-amber-600/50 bg-amber-50/90 dark:bg-amber-950/25 p-4'>
            <p className='text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200 mb-2'>Action</p>
            <div className={RICH_HTML_ACTION_BOX} dangerouslySetInnerHTML={{ __html: trimmedActionHtml }} />
          </div>
        )}

        {showGenericActionRequiredUi && (
          <YesNoFieldset
            legend='Can you provide what is requested above?'
            name={`${baseId}-generic-action`}
            value={genericActionYesNo}
            onChange={setGenericActionYesNo}
          />
        )}

        {refundBlocked && (
          <p role='status' className='text-sm text-amber-800 dark:text-amber-200 mb-4'>
            Your specialist is still confirming the check date(s) and amount(s) in our system. You can answer here once those details are on file.
          </p>
        )}

        {rk === 'letter_105c_sent' && (
          <YesNoFieldset
            legend='Do you have a copy of Letter 105C to share with our technical team?'
            name={`${baseId}-105c`}
            value={hasCopy}
            onChange={setHasCopy}
          />
        )}

        {rk === 'letter_6042c_sent' && (
          <>
            <YesNoFieldset
              legend='Do you have a copy of Letter 6042C?'
              name={`${baseId}-6042`}
              value={has6042Copy}
              onChange={setHas6042Copy}
            />
            {has6042Copy === 'no' && (
              <div className='mt-6 pl-0 sm:pl-2 border-l-2 border-amber-400 dark:border-amber-600 pl-3'>
                <p className='text-sm text-slate-600 dark:text-slate-300 mb-3'>
                  If you do not have a copy of this letter, we can prepare a reconstructed version that you can complete and compile a &quot;Proactive Identity Verification Package&quot;.
                </p>
                <p className='text-sm text-slate-600 dark:text-slate-300 mb-4'>
                  However, we must include your signed 941-X as mandatory supporting documentation to prove the filing is legitimate.
                </p>
                <YesNoFieldset
                  legend='Can you provide a copy of the signed 941-X for this quarter?'
                  name={`${baseId}-941x`}
                  value={canProvide941x}
                  onChange={setCanProvide941x}
                />
              </div>
            )}
          </>
        )}

        {rk === 'refund_previously_issued' && !refundBlocked && (
          <div className='space-y-4'>
            {refundChecksList.map((c, idx) => {
              const id = String(c.refundDetailId || '')
              const amtLabel = formatHoldRefundUsd(c.amount)
              const dateLabel = formatHoldRefundDate(c.refundDate)
              const datePhrase = dateLabel ? ` issued ${dateLabel}` : ''
              const legend = refundChecksList.length > 1
                ? `Payment ${idx + 1} (${amtLabel}${datePhrase}): Did your business receive and deposit this IRS payment?`
                : `Did your business receive and deposit this IRS payment (${amtLabel}${datePhrase})?`
              return (
                <YesNoFieldset
                  key={id || `refund-row-${idx}`}
                  legend={legend}
                  name={`${baseId}-refund-${id}`}
                  value={receivedByCheckId[id] || ''}
                  onChange={(v) => setReceivedByCheckId((prev) => ({ ...prev, [id]: v }))}
                />
              )
            })}
          </div>
        )}

        {show105cUpload && (
          <div className='mt-6 w-full'>
            <label
              htmlFor={`${baseId}-105c-file`}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (uploadBusy) return
                const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : []
                for (const f of files) {
                  if (f.type === 'application/pdf' || (f.type && f.type.startsWith('image/'))) void onUpload(f)
                }
              }}
              className={`w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-3xl p-6 md:p-10 flex flex-col items-center justify-center text-center bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-300 shadow-sm group ${
                uploadBusy ? 'opacity-60 pointer-events-none cursor-wait' : 'cursor-pointer'
              }`}
            >
              <CloudUploadIcon
                size={32}
                className='text-slate-400 group-hover:text-ertc-primary dark:text-slate-500 transition-colors mb-3'
                aria-hidden='true'
              />
              <p className='text-xl font-bold text-slate-700 dark:text-gray-100 mb-1 group-hover:text-ertc-primary transition-colors'>
                Upload Letter 105C
              </p>
              <p className='text-sm text-slate-500 dark:text-slate-400'>Drag and drop files here or click to browse</p>
              <p className='text-xs text-slate-500 dark:text-slate-400 mt-2'>PDF or image.</p>
            </label>
            <input
              id={`${baseId}-105c-file`}
              type='file'
              multiple
              accept='application/pdf,image/*'
              className='hidden'
              disabled={uploadBusy}
              onChange={async (e) => {
                const list = e.target.files ? Array.from(e.target.files) : []
                e.target.value = ''
                for (const f of list) await onUpload(f)
              }}
            />
            <div className='w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 mt-6 transition-colors duration-300 min-h-[150px] flex flex-col justify-center gap-4'>
              {hold105cDocuments.length === 0 ? (
                <p className='text-slate-500 dark:text-slate-400 text-center italic'>
                  No documents uploaded for this category yet.
                </p>
              ) : (
                <ul className='space-y-4'>
                  {hold105cDocuments.map((doc) => (
                    <DocumentRow
                      key={doc.documentId}
                      applicationId={applicationId}
                      _document={doc}
                      setDocuments={setHold105cDocuments}
                      handleCheckDocuments={refreshHold105cDocuments}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {submitError && (
          <div role='alert' className='mt-4 text-sm text-red-700 dark:text-red-300'>{submitError}</div>
        )}

        <div className='mt-8 flex flex-col sm:flex-row gap-3'>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={submitting || !canSubmitResponses()}
            className='py-3 px-6 rounded-xl bg-ertc-primary text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {submitting ? 'Saving…' : 'Submit responses'}
          </button>
        </div>
      </div>
    </div>
  )
}

function YesNoFieldset({ legend, name, value, onChange }) {
  return (
    <fieldset className='mt-6 border border-gray-100 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800'>
      <legend className='text-sm font-semibold text-slate-800 dark:text-gray-100 px-1'>{legend}</legend>
      <div className='flex flex-wrap gap-3 mt-3'>
        <ChoiceButton id={`${name}-yes`} pressed={value === 'yes'} label='Yes' onClick={() => onChange('yes')} />
        <ChoiceButton id={`${name}-no`} pressed={value === 'no'} label='No' onClick={() => onChange('no')} />
      </div>
    </fieldset>
  )
}

function ChoiceButton({ id, pressed, label, onClick }) {
  return (
    <button
      type='button'
      id={id}
      aria-pressed={pressed}
      onClick={onClick}
      className={`min-h-[44px] min-w-[44px] px-6 rounded-lg font-medium border-2 transition-colors ${
        pressed
          ? 'border-ertc-primary bg-ertc-primary text-white'
          : 'border-slate-200 dark:border-slate-600 text-slate-800 dark:text-gray-100 hover:border-ertc-primary'
      }`}
    >
      {label}
    </button>
  )
}
